import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  getAllFeatures,
  toggleFeature,
} from "../../Api_Qdrant/Fast_apis";
import { getFastApiErrorMessage } from "../../Api_Qdrant/fastApiError";
import "./Features.css";

function FeatureStatus({ isActive }) {
  return (
    <span
      className={`feature-status ${
        isActive
          ? "feature-status--active"
          : "feature-status--inactive"
      }`}
    >
      <span className="feature-status-dot" />

      {isActive ? "Active" : "Inactive"}
    </span>
  );
}

function FeatureToggle({ isActive, loading, onChange }) {
  return (
    <button
      type="button"
      className={`feature-toggle ${
        isActive ? "feature-toggle--active" : ""
      }`}
      onClick={onChange}
      disabled={loading}
      aria-label={
        isActive
          ? "Deactivate feature"
          : "Activate feature"
      }
      aria-pressed={isActive}
    >
      <span className="feature-toggle-track">
        <span className="feature-toggle-thumb" />
      </span>
    </button>
  );
}

export default function Features() {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  /*
   * Fetch all features
   */
  const fetchFeatures = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await getAllFeatures();

      // Rate-limited: the interceptor already showed a friendly toast — just stop.
      if (response?.rateLimited) {
        setFeatures([]);
        return;
      }

      if (response?.success) {
        /*
         * Your API:
         *
         * {
         *   success: true,
         *   data: [
         *     {
         *       feature_id: 1,
         *       feature_name: "human_agent_connectivity",
         *       is_active: true
         *     }
         *   ]
         * }
         */

        setFeatures(response.data || []);
      } else {
        toast.error(
          getFastApiErrorMessage(response, "Failed to fetch features.")
        );

        setFeatures([]);
      }
    } catch (error) {
      console.error(
        "Failed to fetch features:",
        error
      );

      toast.error(
        getFastApiErrorMessage(error, "Failed to fetch features.")
      );

      setFeatures([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  /*
   * Initial load
   */
  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  /*
   * Toggle feature
   */
  const handleToggle = async (feature) => {
    const featureId = feature.feature_id;

    // Current state -> opposite state
    const newStatus = !feature.is_active;

    setTogglingId(featureId);

    try {
      const response = await toggleFeature(
        featureId,
        newStatus
      );

      /*
       * Your toggle API returns:
       *
       * {
       *   success: true,
       *   message: "Feature updated",
       *   data: {
       *     feature_id: 1,
       *     feature_name: "human_agent_connectivity",
       *     is_active: true
       *   }
       * }
       */

      // Rate-limited: the interceptor already showed a friendly toast — just stop.
      if (response?.rateLimited) return;

      if (response?.success && response?.data) {
        const updatedFeature = response.data;

        /*
         * Update only the feature that was toggled.
         */
        setFeatures((currentFeatures) =>
          currentFeatures.map((item) =>
            item.feature_id === updatedFeature.feature_id
              ? {
                  ...item,
                  ...updatedFeature,
                }
              : item
          )
        );

        toast.success(
          response.message ||
            `Feature ${
              updatedFeature.is_active
                ? "activated"
                : "deactivated"
            } successfully.`
        );

        // Re-pull the list from the server so it reflects the real state
        // (the optimistic update above only patches the toggled row).
        await fetchFeatures(true);
      } else {
        toast.error(
          getFastApiErrorMessage(response, "Failed to update feature.")
        );
      }
    } catch (error) {
      console.error(
        "Failed to toggle feature:",
        error
      );

      toast.error(
        getFastApiErrorMessage(error, "Failed to update feature.")
      );
    } finally {
      setTogglingId(null);
    }
  };

  const activeCount = features.filter(
    (feature) => feature.is_active
  ).length;

  const inactiveCount =
    features.length - activeCount;

  return (
    <div className="features-page">

      {/* =====================================
          HEADER
      ====================================== */}

      <header className="features-header">
        <div className="features-title-section">

          <div className="features-title-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M12 3L14.2 8.3L20 9.1L15.8 13.1L16.9 18.9L12 16.1L7.1 18.9L8.2 13.1L4 9.1L9.8 8.3L12 3Z"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div>
            <h1>Feature Management</h1>

            <p className="features-subtitle">
              Manage and control features available
              across the platform.
            </p>
          </div>

        </div>

        <button
          type="button"
          className="features-refresh-btn"
          onClick={() => fetchFeatures(true)}
          disabled={loading || refreshing}
        >
          <svg
            className={
              refreshing ? "features-refresh-spin" : ""
            }
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M20 11A8.1 8.1 0 0 0 5.4 6.4L4 8"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            <path
              d="M4 4V8H8"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            <path
              d="M4 13A8.1 8.1 0 0 0 18.6 17.6L20 16"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            <path
              d="M20 20V16H16"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          {refreshing ? "Refreshing..." : "Refresh"}
        </button>
      </header>


      {/* =====================================
          SUMMARY
      ====================================== */}

      {!loading && (
        <section className="features-summary">

          {/* Total */}
          <div className="features-summary-card">

            <div className="features-summary-icon total">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v11a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5v-11Z"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />

                <path
                  d="M8 8h8M8 12h8M8 16h5"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            <div>
              <span>Total Features</span>
              <strong>{features.length}</strong>
            </div>

          </div>


          {/* Active */}
          <div className="features-summary-card">

            <div className="features-summary-icon active">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M20 6L9 17l-5-5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <div>
              <span>Active</span>
              <strong>{activeCount}</strong>
            </div>

          </div>


          {/* Inactive */}
          <div className="features-summary-card">

            <div className="features-summary-icon inactive">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            <div>
              <span>Inactive</span>
              <strong>{inactiveCount}</strong>
            </div>

          </div>

        </section>
      )}


      {/* =====================================
          FEATURES TABLE
      ====================================== */}

      <section className="features-card">

        <div className="features-card-header">
          <div>
            <h2>Platform Features</h2>

            <p>
              Enable or disable features for the
              application.
            </p>
          </div>
        </div>


        <div className="features-table-wrapper">

          {loading ? (
            <div className="features-state">

              <div className="features-loader" />

              <span>
                Loading features...
              </span>

            </div>
          ) : features.length === 0 ? (
            <div className="features-state">

              <div className="features-empty-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M12 3L14.2 8.3L20 9.1L15.8 13.1L16.9 18.9L12 16.1L7.1 18.9L8.2 13.1L4 9.1L9.8 8.3L12 3Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              <h3>No features found</h3>

              <p>
                There are currently no features
                available to manage.
              </p>

            </div>
          ) : (
            <table className="features-table">

              <colgroup>
                <col className="feature-column-id" />
                <col className="feature-column-name" />
                <col className="feature-column-status" />
                <col className="feature-column-action" />
              </colgroup>

              <thead>
                <tr>
                  <th>SN</th>
                  <th>Feature</th>
                  <th>Status</th>
                  <th className="feature-action-header">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>

                {features.map((feature) => {

                  const isToggling =
                    togglingId === feature.feature_id;

                  return (
                    <tr key={feature.feature_id}>

                      {/* ID */}
                      <td className="feature-id">
                        #{feature.feature_id}
                      </td>


                      {/* Feature name */}
                      <td>
                        <div className="feature-name-wrapper">

                          <span
                            className={`feature-indicator ${
                              feature.is_active
                                ? "feature-indicator-active"
                                : "feature-indicator-inactive"
                            }`}
                          />

                          <span
                            className="feature-name"
                            title={feature.feature_name}
                          >
                            {feature.feature_name}
                          </span>

                        </div>
                      </td>


                      {/* Status */}
                      <td>
                        <FeatureStatus
                          isActive={
                            feature.is_active
                          }
                        />
                      </td>


                      {/* Toggle */}
                      <td className="feature-action-cell">

                        <div className="feature-toggle-wrapper">

                          <FeatureToggle
                            isActive={
                              feature.is_active
                            }
                            loading={isToggling}
                            onChange={() =>
                              handleToggle(feature)
                            }
                          />

                          <span
                            className={`feature-action-label ${
                              feature.is_active
                                ? "feature-action-active"
                                : ""
                            }`}
                          >
                            {isToggling
                              ? "Updating..."
                              : feature.is_active
                              ? "Enabled"
                              : "Disabled"}
                          </span>

                        </div>

                      </td>

                    </tr>
                  );
                })}

              </tbody>
            </table>
          )}

        </div>

      </section>

    </div>
  );
}